/**
 * Plugin-specific todo detail renderer for Home Maintenance.
 * Renders service call context (contractor info, appliance, cost).
 */

import React from 'react';
import { TodoRendererProps } from '@/infra/plugin-registry/types';

interface ServiceCallContext {
  appliance_id?: string;
  appliance_name?: string;
  contractor_name?: string;
  contractor_phone?: string;
  service_type?: string;
  estimated_cost?: number;
}

// context_blob may be a JSON string (legacy) or an already-parsed object; the
// UI-only `context` field is the parsed fallback. Resolve to a plain object.
function parseContext<T>(todo: TodoRendererProps['todo']): T {
  try {
    if (todo.context_blob) {
      return typeof todo.context_blob === 'string'
        ? (JSON.parse(todo.context_blob) as T)
        : (todo.context_blob as unknown as T);
    }
    if (todo.context) {
      return todo.context as T;
    }
  } catch {
    // Invalid context — fall through to empty.
  }
  return {} as T;
}

export const ServiceCallRenderer: React.FC<TodoRendererProps> = ({ todo }) => {
  const context = parseContext<ServiceCallContext>(todo);

  return (
    <div className="maintenance-detail" data-testid="maintenance-detail">
      {context.appliance_name && (
        <div className="maintenance-detail__field">
          <span className="maintenance-detail__label">Appliance:</span>
          <span>{context.appliance_name}</span>
        </div>
      )}
      {context.contractor_name && (
        <div className="maintenance-detail__field">
          <span className="maintenance-detail__label">Contractor:</span>
          <span>{context.contractor_name}</span>
        </div>
      )}
      {context.contractor_phone && (
        <div className="maintenance-detail__field">
          <span className="maintenance-detail__label">Phone:</span>
          <a href={`tel:${context.contractor_phone}`}>{context.contractor_phone}</a>
        </div>
      )}
      {context.service_type && (
        <div className="maintenance-detail__field">
          <span className="maintenance-detail__label">Service Type:</span>
          <span>{context.service_type}</span>
        </div>
      )}
      {context.estimated_cost != null && (
        <div className="maintenance-detail__field">
          <span className="maintenance-detail__label">Est. Cost:</span>
          <span>${context.estimated_cost.toFixed(2)}</span>
        </div>
      )}
    </div>
  );
};

// context_blob for type_tag "maintenance.task" — a generic maintenance task
// linked to an appliance. Shares the appliance fields with service calls but
// carries no contractor/cost.
interface MaintenanceTaskContext {
  appliance_id?: string;
  appliance_name?: string;
  service_type?: string;
  location?: string;
}

export const MaintenanceTaskRenderer: React.FC<TodoRendererProps> = ({ todo }) => {
  const context = parseContext<MaintenanceTaskContext>(todo);

  return (
    <div className="maintenance-detail" data-testid="maintenance-task-detail">
      {context.appliance_name && (
        <div className="maintenance-detail__field">
          <span className="maintenance-detail__label">Appliance:</span>
          <span>{context.appliance_name}</span>
        </div>
      )}
      {context.service_type && (
        <div className="maintenance-detail__field">
          <span className="maintenance-detail__label">Task Type:</span>
          <span>{context.service_type}</span>
        </div>
      )}
      {context.location && (
        <div className="maintenance-detail__field">
          <span className="maintenance-detail__label">Location:</span>
          <span>{context.location}</span>
        </div>
      )}
    </div>
  );
};
